import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {CommandService, ICommandInterceptor} from '@core/services/command.service';
import {SocketClusterService} from '@core/services/socket-cluster.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {PlatformCommand, PlatformCommandResponse} from '@shared/commands/command-base';
import {EventStatus, NotificationMessageModel} from '@shared/models/notification-message.model';
import {rememberHandOffReturn, takeHandOffReturn} from '@core/services/handoff-return';

/**
 * EAT-E-44-03 (ADR 0189) — BACK TO WHERE THE HAND-OFF TOOK THE TAB FROM. A scripted hand-off pulls the member's tab
 * off its page (a mission's tab, off the mission) onto the next node's page, and a server-route launch publishes the
 * later navigations on a channel no tab hears, so after the person's own Mark as Complete nothing brought the tab back.
 *
 * One command interceptor (XCT-E-378-02), scoped to `MarkAsCompletedCommand` and nothing else:
 * - the unattended navigation that opens the node page is remembered (where the tab was, where it is taken);
 * - before a press is sent, the page it is sent from is noted — its answer can arrive after the NEXT hand-off has
 *   already moved the tab (seen live: a four-second scripted node between two human nodes);
 * - when the press is answered `Success` with nothing of its own to show (no redirect, no dialog such as the
 *   pending-checklist confirmation, no error), the tab goes back. Once per press: the same answer is seen over HTTP
 *   and again as the websocket echo. A press whose answer redirects, and a page reached by hand, behave as before.
 */
@Injectable({providedIn: 'root'})
export class HandOffReturnInterceptor implements ICommandInterceptor {
  private static readonly PRESS = 'MarkAsCompletedCommand';

  /** The page each press was sent from, by command object (the id is minted after the interceptors run). */
  private readonly sentFrom = new WeakMap<PlatformCommand, string>();
  /** The presses this tab has already been sent back for. */
  private readonly returned = new WeakSet<PlatformCommand>();

  constructor(private readonly commands: CommandService,
              private readonly sockets: SocketClusterService,
              private readonly router: Router,
              private readonly splash: FuseSplashScreenService) {
    this.commands.registerCommandInterceptor(this);
    this.sockets.onUnattendedNavigation().subscribe((message: NotificationMessageModel) => this.remember(message));
  }

  onBeforePlatformCommandExecution(command: PlatformCommand): boolean {
    if (command?.name === HandOffReturnInterceptor.PRESS) {
      this.sentFrom.set(command, this.router.url);
    }
    return true;
  }

  onAfterPlatformCommandExecution(command: PlatformCommand, response: PlatformCommandResponse): void {
    // Only the Success answer: the same press is also seen as a Pending interim, before any dialog it opens is confirmed.
    if (command?.name !== HandOffReturnInterceptor.PRESS || !response || response.status !== EventStatus.Success) {
      return;
    }
    const fieldValues = response.fieldValues || {};
    if (fieldValues['RedirectUrl'] || fieldValues['RedirectCommand'] || fieldValues['ReplaceWindowUrl']
      || fieldValues['PlatformDialogId'] || fieldValues['SnackBarErrorMessage']) {
      return;
    }
    if (this.returned.has(command)) {
      return;
    }
    const back = takeHandOffReturn(this.sentFrom.get(command) || this.router.url);
    if (!back) {
      return;
    }
    this.returned.add(command);
    this.splash.hide();
    this.router.navigateByUrl(back);
  }

  /** The hand-off's navigation response, as CommandService follows it: remember the page the tab is pulled off. */
  private remember(message: NotificationMessageModel): void {
    const response = new PlatformCommandResponse(message?.Payload);
    const redirectUrl = response.fieldValues && response.fieldValues['RedirectUrl'];
    if (redirectUrl) {
      rememberHandOffReturn(this.router.url, redirectUrl);
    }
  }
}
